import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Sparkles, AlertCircle } from "lucide-react";

/**
 * Threshold Playground (Plano v17.07 §V.4 P1).
 * Usuário cola um sample de scores históricos (intent_score, ou similar)
 * e ajusta o threshold com slider. Mostra precision/recall estimado e conta acima/abaixo.
 */
export function ThresholdPlayground({
  samplesText,
  onSamplesChange,
}: {
  samplesText?: string;
  onSamplesChange?: (s: string) => void;
}) {
  const [text, setText] = useState(samplesText ?? "0.92,0.88,0.71,0.65,0.5,0.43,0.31,0.85,0.74,0.62");
  const [threshold, setThreshold] = useState(0.7);
  const [labelsText, setLabelsText] = useState("1,1,1,0,0,0,0,1,1,0");

  const scores = useMemo(
    () => text.split(/[,\s]+/).map((s) => Number(s)).filter((n) => Number.isFinite(n)),
    [text]
  );
  const labels = useMemo(
    () => labelsText.split(/[,\s]+/).map((s) => (s.trim() === "1" ? 1 : 0)),
    [labelsText]
  );

  const stats = useMemo(() => {
    let tp = 0, fp = 0, tn = 0, fn = 0;
    scores.forEach((s, i) => {
      const pred = s >= threshold ? 1 : 0;
      const truth = labels[i] ?? 0;
      if (pred === 1 && truth === 1) tp++;
      else if (pred === 1 && truth === 0) fp++;
      else if (pred === 0 && truth === 0) tn++;
      else fn++;
    });
    const precision = tp + fp === 0 ? 0 : tp / (tp + fp);
    const recall = tp + fn === 0 ? 0 : tp / (tp + fn);
    const f1 = precision + recall === 0 ? 0 : (2 * precision * recall) / (precision + recall);
    const above = scores.filter((s) => s >= threshold).length;
    return { tp, fp, tn, fn, precision, recall, f1, above, below: scores.length - above };
  }, [scores, labels, threshold]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Threshold Playground</CardTitle>
        <CardDescription>Simule o impacto do threshold em precision / recall antes de promover.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Scores (vírgula)</Label>
            <Input value={text} onChange={(e) => { setText(e.target.value); onSamplesChange?.(e.target.value); }} className="font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">Labels reais 0/1 (vírgula)</Label>
            <Input value={labelsText} onChange={(e) => setLabelsText(e.target.value)} className="font-mono text-xs" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Threshold: <span className="font-mono">{threshold.toFixed(2)}</span></Label>
            <span className="text-xs text-muted-foreground">{stats.above} acima · {stats.below} abaixo</span>
          </div>
          <Slider value={[threshold]} min={0} max={1} step={0.01} onValueChange={(v) => setThreshold(v[0])} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Metric label="Precision" value={stats.precision} />
          <Metric label="Recall" value={stats.recall} />
          <Metric label="F1" value={stats.f1} />
          <div className="rounded-md border p-2 text-center">
            <div className="text-[10px] uppercase text-muted-foreground">TP / FP / FN</div>
            <div className="font-mono text-sm">{stats.tp} / {stats.fp} / {stats.fn}</div>
          </div>
        </div>

        {scores.length !== labels.length && (
          <div className="flex items-center gap-2 text-xs text-amber-600">
            <AlertCircle className="w-4 h-4" /> Quantidade de scores ≠ labels. Estatísticas podem estar incorretas.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  const pct = (value * 100).toFixed(1);
  const color = value >= 0.8 ? "default" : value >= 0.6 ? "secondary" : "destructive";
  return (
    <div className="rounded-md border p-2 text-center">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <Badge variant={color as "default" | "secondary" | "destructive"} className="mt-1">{pct}%</Badge>
    </div>
  );
}
